import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router';
import { api, type Webhook, type WebhookEventType } from '../api';
import { PlusIcon, WebhookIcon } from '../components/icons';
import {
  CodeButton,
  CopyButton,
  Dialog,
  Empty,
  ErrorNote,
  PageHeader,
  RowMenu,
  SkeletonRows,
  Status,
  timeAgo,
  toneOf,
  useToast,
} from '../components/ui';
import { useLoad } from '../hooks';
import { useSession } from '../session';

export const EVENTS: { type: WebhookEventType; label: string }[] = [
  { type: 'email.sent', label: 'Sent' },
  { type: 'email.delivered', label: 'Delivered' },
  { type: 'email.delivery_delayed', label: 'Delivery delayed' },
  { type: 'email.bounced', label: 'Bounced' },
  { type: 'email.complained', label: 'Complained' },
  { type: 'email.rejected', label: 'Rejected' },
  { type: 'email.rendering_failure', label: 'Rendering failed' },
  { type: 'email.suppressed', label: 'Suppressed' },
];

const VERIFY = `import { verifyWebhook } from 'vaartix';

// Express: app.post('/webhooks/vaartix', express.raw({ type: 'application/json' }), ...)
const event = verifyWebhook({
  payload: req.body,          // the raw body
  headers: req.headers,
  secret: process.env.VAARTIX_WEBHOOK_SECRET,
});

if (event.type === 'email.bounced') {
  // stop emailing event.data.recipient
}`;

export function Webhooks() {
  const navigate = useNavigate();
  const { team } = useSession();
  const canEdit = team.role !== 'member';
  const { data, error, loading, reload } = useLoad(
    () => api<{ data: Webhook[] }>('GET', '/webhooks'),
    [],
  );
  const [creating, setCreating] = useState(false);
  const [secret, setSecret] = useState<{ id: string; secret: string } | null>(null);
  const [deleting, setDeleting] = useState<Webhook | null>(null);
  const toast = useToast();
  const hooks = data?.data ?? [];

  async function setStatus(hook: Webhook, status: 'enabled' | 'disabled') {
    try {
      await api('PATCH', `/webhooks/${encodeURIComponent(hook.id)}`, { status });
      toast.show(status === 'enabled' ? 'Endpoint turned on' : 'Endpoint turned off');
      reload();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not update');
    }
  }

  return (
    <>
      <PageHeader
        title="Webhooks"
        description="Get a signed request on your server for deliveries, bounces and complaints."
        actions={
          <>
            {canEdit ? (
              <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
                <PlusIcon />
                Add endpoint
              </button>
            ) : null}
            <CodeButton
              title="Verify webhook requests"
              snippets={[{ label: 'Node.js', code: VERIFY }]}
            />
          </>
        }
      />
      <ErrorNote error={error} />

      {!loading && hooks.length === 0 ? (
        <Empty
          icon={<WebhookIcon />}
          title="No endpoints yet"
          actions={
            canEdit ? (
              <>
                <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
                  <PlusIcon />
                  Add endpoint
                </button>
                <a
                  className="btn btn-ghost"
                  href="https://vaartix.com/docs/webhooks/setup/"
                  target="_blank"
                  rel="noreferrer"
                >
                  Read the guide
                </a>
              </>
            ) : null
          }
        >
          <p>
            Stop polling for status. Vaartix posts each event to your server, signed, and retries
            for a day if it's down.
          </p>
        </Empty>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Endpoint</th>
                <th>Status</th>
                <th className="hide-sm">Events</th>
                <th className="nowrap hide-sm">Created</th>
                <th style={{ width: 48 }} />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <SkeletonRows cols={5} rows={2} />
              ) : (
                hooks.map((hook) => (
                  <tr
                    key={hook.id}
                    className="row-link"
                    tabIndex={0}
                    onClick={() => navigate(`/webhooks/${hook.id}`)}
                    onKeyDown={(e) => e.key === 'Enter' && navigate(`/webhooks/${hook.id}`)}
                  >
                    <td className="truncate" style={{ maxWidth: 420 }}>
                      <div className="cell-main">
                        <span className={`row-icon ${toneOf(hook.status)}`}>
                          <WebhookIcon />
                        </span>
                        <strong className="mono" style={{ fontSize: 13 }}>
                          {hook.url}
                        </strong>
                      </div>
                    </td>
                    <td>
                      <Status status={hook.status} />
                    </td>
                    <td className="hide-sm muted">
                      {hook.events.length === EVENTS.length
                        ? 'All events'
                        : `${hook.events.length} events`}
                    </td>
                    <td className="nowrap hide-sm muted">{timeAgo(hook.createdAt)}</td>
                    <td className="right">
                      {canEdit ? (
                        <RowMenu
                          items={[
                            {
                              label: 'View deliveries',
                              onSelect: () => navigate(`/webhooks/${hook.id}`),
                            },
                            hook.status === 'enabled'
                              ? {
                                  label: 'Turn off',
                                  onSelect: () => void setStatus(hook, 'disabled'),
                                }
                              : {
                                  label: 'Turn on',
                                  onSelect: () => void setStatus(hook, 'enabled'),
                                },
                            { label: 'Delete', danger: true, onSelect: () => setDeleting(hook) },
                          ]}
                        />
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {creating ? (
        <WebhookForm
          onClose={() => setCreating(false)}
          onSaved={(hook) => {
            setCreating(false);
            if (hook.secret) setSecret({ id: hook.id, secret: hook.secret });
            reload();
          }}
        />
      ) : null}
      {secret ? <SecretDialog secret={secret.secret} onClose={() => setSecret(null)} /> : null}
      {deleting ? (
        <Dialog
          title="Delete this endpoint?"
          description="Vaartix stops sending to it immediately, and its delivery log is deleted."
          onClose={() => setDeleting(null)}
          footer={
            <>
              <button type="button" className="btn btn-ghost" onClick={() => setDeleting(null)}>
                Keep it
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={async () => {
                  const hook = deleting;
                  setDeleting(null);
                  try {
                    await api('DELETE', `/webhooks/${encodeURIComponent(hook.id)}`);
                    toast.show('Endpoint deleted');
                    reload();
                  } catch (err) {
                    toast.show(err instanceof Error ? err.message : 'Could not delete');
                  }
                }}
              >
                Delete endpoint
              </button>
            </>
          }
        />
      ) : null}
      {toast.node}
    </>
  );
}

export function SecretDialog({ secret, onClose }: { secret: string; onClose: () => void }) {
  return (
    <Dialog
      title="Copy your signing secret"
      description="Use it to verify that requests come from Vaartix. It's shown only now; you can rotate it later if needed."
      onClose={onClose}
      footer={
        <button type="button" className="btn btn-primary" onClick={onClose}>
          I've saved it
        </button>
      }
    >
      <div className="secret-box">
        <code>{secret}</code>
        <CopyButton value={secret} label="Copy secret" />
      </div>
      <p className="dim" style={{ margin: 0, fontSize: 13 }}>
        Store it on your server, for example as <code>VAARTIX_WEBHOOK_SECRET</code>.
      </p>
    </Dialog>
  );
}

export function WebhookForm({
  hook,
  onClose,
  onSaved,
}: {
  hook?: Webhook;
  onClose: () => void;
  onSaved: (hook: Webhook & { secret?: string }) => void;
}) {
  const [url, setUrl] = useState(hook?.url ?? '');
  const [events, setEvents] = useState<WebhookEventType[]>(
    hook?.events ?? ['email.delivered', 'email.bounced', 'email.complained'],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const saved = hook
        ? await api<Webhook>('PATCH', `/webhooks/${encodeURIComponent(hook.id)}`, {
            url: url.trim(),
            events,
          })
        : await api<Webhook & { secret: string }>('POST', '/webhooks', { url: url.trim(), events });
      onSaved(saved);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  const toggle = (type: WebhookEventType) =>
    setEvents((list) => (list.includes(type) ? list.filter((t) => t !== type) : [...list, type]));

  return (
    <Dialog
      wide
      title={hook ? 'Edit endpoint' : 'Add a webhook endpoint'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            form="webhook-form"
            className="btn btn-primary"
            disabled={busy || !url.trim() || events.length === 0}
          >
            {busy ? 'Saving…' : hook ? 'Save' : 'Add endpoint'}
          </button>
        </>
      }
    >
      <form id="webhook-form" onSubmit={submit} style={{ display: 'contents' }}>
        <div className="field">
          <label htmlFor="hook-url">Endpoint URL</label>
          <input
            id="hook-url"
            className="input"
            placeholder="https://api.yourapp.in/webhooks/vaartix"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <span className="hint">
            A public https:// address. Testing locally? Use a tunnel such as cloudflared or ngrok.
          </span>
        </div>
        <div className="field">
          <div className="label" style={{ display: 'flex', justifyContent: 'space-between' }}>
            Events
            <button
              type="button"
              className="link-btn"
              onClick={() =>
                setEvents(events.length === EVENTS.length ? [] : EVENTS.map((e) => e.type))
              }
            >
              {events.length === EVENTS.length ? 'Clear all' : 'Select all'}
            </button>
          </div>
          <div className="checks-grid">
            {EVENTS.map((event) => (
              <label key={event.type}>
                <input
                  type="checkbox"
                  checked={events.includes(event.type)}
                  onChange={() => toggle(event.type)}
                />
                <span>
                  {event.label}{' '}
                  <span className="dim mono" style={{ fontSize: 11.5 }}>
                    {event.type}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </div>
        <ErrorNote error={error} />
      </form>
    </Dialog>
  );
}
